import logger from '../../../utils/logger';
/**
 * Display Service - Communicates with Front Desk Display via SSE
 * Triggers checkout events on customer-facing display
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

// Display pushes are authorised by the POS staff session
const authHeaders = () => {
  const token = localStorage.getItem('pos_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Get terminal ID from settings or default
export const getTerminalId = () => {
  return localStorage.getItem('pos_terminal_id') || 'T001';
};

export const setTerminalId = (terminalId) => {
  localStorage.setItem('pos_terminal_id', terminalId);
};

/**
 * Notify display that checkout has started
 * Shows order details on customer-facing display
 */
export const startCheckout = async ({ 
  orderNumber, 
  orderId,
  items, 
  subtotal, 
  tax, 
  total, 
  paymentMethod,
  paymentMethodsAvailable,
  customerName,
  tableNumber 
}) => {
  try {
    const response = await fetch(`${API}/api/display/checkout/start`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        terminal_id: getTerminalId(),
        order_id: orderId,
        order_number: orderNumber,
        subtotal,
        tax,
        total,
        items: items.map(item => ({
          name: item.name,
          qty: item.quantity,
          price: (item.price + (item.modifierTotal || 0)) * item.quantity
        })),
        payment_method: paymentMethod,
        payment_methods_available: paymentMethodsAvailable || ['card', 'cash', 'qr', 'zelle', 'venmo', 'gift', 'loyalty', 'crypto'],
        customer_name: customerName || 'Guest',
        table_number: tableNumber
      })
    });
    
    const data = await response.json();
    logger.log('[Display] Checkout started:', data);
    return data;
  } catch (error) {
    console.error('[Display] Failed to start checkout:', error);
    // Don't throw - display is optional
    return null;
  }
};

/**
 * Update checkout status on display
 * @param {string} status - processing | waiting_for_card | card_inserted | processing_payment
 * @param {string} message - Optional custom message
 */
export const updateCheckout = async (status, message = null) => {
  try {
    const response = await fetch(`${API}/api/display/checkout/update`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        terminal_id: getTerminalId(),
        status,
        message
      })
    });
    
    const data = await response.json();
    logger.log('[Display] Checkout updated:', data);
    return data;
  } catch (error) {
    console.error('[Display] Failed to update checkout:', error);
    return null;
  }
};

/**
 * End checkout session on display
 * @param {string} result - approved | declined | cancelled | error
 * @param {string} message - Optional result message
 * @param {object} receiptData - Optional receipt info (card_type, last_four, auth_code)
 */
export const endCheckout = async (result, message = null, receiptData = null) => {
  try {
    const response = await fetch(`${API}/api/display/checkout/end`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        terminal_id: getTerminalId(),
        result,
        message,
        receipt_data: receiptData
      })
    });
    
    const data = await response.json();
    logger.log('[Display] Checkout ended:', data);
    return data;
  } catch (error) {
    console.error('[Display] Failed to end checkout:', error);
    return null;
  }
};

// Convenience methods
export const displayService = {
  getTerminalId,
  setTerminalId,
  startCheckout,
  updateCheckout,
  endCheckout,
  
  // Status shortcuts
  showProcessing: () => updateCheckout('processing'),
  showWaitingForCard: () => updateCheckout('waiting_for_card', 'Please insert or tap your card'),
  showCardInserted: () => updateCheckout('card_inserted', 'Reading card...'),
  showProcessingPayment: () => updateCheckout('processing_payment', 'Processing payment...'),
  
  // Result shortcuts
  showApproved: (receiptData) => endCheckout('approved', 'Payment successful!', receiptData),
  showDeclined: (message) => endCheckout('declined', message || 'Payment declined'),
  showCancelled: () => endCheckout('cancelled', 'Transaction cancelled'),
  showError: (message) => endCheckout('error', message || 'Transaction error')
};

export default displayService;
